import React, { useEffect, useState, useCallback } from 'react';
import {
  getMoodleConfig,
  getExamProfile, updateExamProfile, changeExamPassword,
} from '../api/examApi';
import ExamHeader from '../components/layout/ExamHeader';

// ── Accessibility preference helpers ──────────────────────────────────────────
const PREFS_KEY = 'examAccessibility';

const DEFAULT_PREFS = {
  theme: 'light',
  fontSize: 'md',
  reduceMotion: false,
  dyslexiaFont: false,
  focusIndicators: false,
};

function loadPrefs() {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
  catch { return DEFAULT_PREFS; }
}

function savePrefsToStorage(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function applyAccessibilityPrefs(prefs) {
  const root = document.documentElement;
  root.setAttribute('data-exam-theme', prefs.theme || 'light');
  root.setAttribute('data-exam-fontsize', prefs.fontSize || 'md');
  root.setAttribute('data-exam-dyslexia', prefs.dyslexiaFont ? 'true' : 'false');
  root.setAttribute('data-exam-reduce-motion', prefs.reduceMotion ? 'true' : 'false');
  root.setAttribute('data-exam-focus', prefs.focusIndicators ? 'enhanced' : 'default');
}

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Section({ title, desc, children }) {
  return (
    <div style={{
      background: 'var(--exam-surface)',
      border: '1px solid var(--exam-border)',
      borderRadius: 12,
      padding: '24px 28px',
      marginBottom: 24,
      boxShadow: 'var(--exam-shadow-xs)',
    }}>
      <div style={{ marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--exam-border)' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--exam-text)', letterSpacing: '-0.01em' }}>
          {title}
        </h3>
        {desc && <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--exam-text-muted)' }}>{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 600,
        color: 'var(--exam-text-secondary)', marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</label>
      {children}
      {hint && <p style={{ margin: '5px 0 0', fontSize: 11.5, color: 'var(--exam-text-muted)', lineHeight: 1.5 }}>{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, userSelect: 'none' }}>
      <div
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        style={{
          width: 38, height: 22, borderRadius: 11, flexShrink: 0,
          background: checked ? 'var(--exam-primary)' : 'var(--exam-border)',
          position: 'relative', transition: 'background 150ms', cursor: 'pointer',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 19 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 150ms', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ color: 'var(--exam-text)' }}>{label}</span>
    </label>
  );
}

function ChipSelector({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
            border: `1.5px solid ${value === opt.value ? 'var(--exam-primary)' : 'var(--exam-border)'}`,
            background: value === opt.value ? 'var(--exam-primary-light)' : 'var(--exam-surface-alt)',
            color: value === opt.value ? 'var(--exam-primary)' : 'var(--exam-text-secondary)',
            transition: 'all 120ms',
          }}
        >
          {opt.icon && <span style={{ marginRight: 5 }}>{opt.icon}</span>}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ExamSettingsPage() {
  // Moodle status (read-only — configured in Tenant Admin)
  const [moodleConfigured, setMoodleConfigured] = useState(false);
  const [moodleEnabled, setMoodleEnabled] = useState(false);
  const [moodleUrl, setMoodleUrl] = useState('');

  // Accessibility
  const [prefs, setPrefs] = useState(loadPrefs);

  // User profile
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', role: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  useEffect(() => {
    getMoodleConfig().then((cfg) => {
      setMoodleUrl(cfg.moodle_url || '');
      setMoodleEnabled(cfg.enabled ?? false);
      setMoodleConfigured(cfg.configured ?? false);
    }).catch(() => {});

    getExamProfile().then(setProfile).catch(() => {});
  }, []);

  useEffect(() => {
    applyAccessibilityPrefs(prefs);
    savePrefsToStorage(prefs);
  }, [prefs]);

  const updatePref = useCallback((key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  const handleSaveProfile = async () => {
    setProfileSaving(true); setProfileMsg('');
    try {
      await updateExamProfile({ first_name: profile.first_name, last_name: profile.last_name });
      setProfileMsg('Profile updated.');
    } catch (e) { setProfileMsg('Error: ' + (e?.response?.data?.detail || e.message)); }
    finally { setProfileSaving(false); }
  };

  const handleChangePassword = async () => {
    if (pwNew !== pwConfirm) { setPwMsg('New passwords do not match.'); return; }
    if (pwNew.length < 8) { setPwMsg('Password must be at least 8 characters.'); return; }
    setPwSaving(true); setPwMsg('');
    try {
      await changeExamPassword({ current_password: pwCurrent, new_password: pwNew });
      setPwMsg('Password changed successfully.');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (e) { setPwMsg('Error: ' + (e?.response?.data?.detail || e.message)); }
    finally { setPwSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ExamHeader breadcrumbs={[{ label: 'Settings' }]} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>

          <div style={{ marginBottom: 28 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--exam-text)' }}>Settings</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--exam-text-muted)' }}>
              Manage your profile, accessibility preferences, and integrations.
            </p>
          </div>

          {/* ── User Profile ── */}
          <Section title="User Profile" desc="Update your display name and change your password.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
              <Field label="First Name">
                <input className="exam-input" value={profile.first_name}
                  onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))} style={{ width: '100%' }} />
              </Field>
              <Field label="Last Name">
                <input className="exam-input" value={profile.last_name}
                  onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))} style={{ width: '100%' }} />
              </Field>
            </div>
            <Field label="Email" hint="Email cannot be changed here. Contact your administrator.">
              <input className="exam-input" value={profile.email} readOnly style={{ width: '100%', opacity: 0.6 }} />
            </Field>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
              <button className="exam-btn exam-btn-primary exam-btn-md" onClick={handleSaveProfile} disabled={profileSaving}>
                {profileSaving ? 'Saving…' : 'Save Profile'}
              </button>
              {profileMsg && (
                <span style={{ fontSize: 13, fontWeight: 500, color: profileMsg.startsWith('Error') ? 'var(--exam-error)' : 'var(--exam-success)' }}>
                  {profileMsg.startsWith('Error') ? '' : '✓ '}{profileMsg}
                </span>
              )}
            </div>

            <div style={{ paddingTop: 20, borderTop: '1px solid var(--exam-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--exam-text)', marginBottom: 14 }}>Change Password</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                <Field label="Current Password">
                  <input className="exam-input" type="password" value={pwCurrent}
                    onChange={(e) => setPwCurrent(e.target.value)} style={{ width: '100%' }} placeholder="Current password" />
                </Field>
                <Field label="New Password">
                  <input className="exam-input" type="password" value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)} style={{ width: '100%' }} placeholder="Min. 8 characters" />
                </Field>
                <Field label="Confirm New Password">
                  <input className="exam-input" type="password" value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)} style={{ width: '100%' }} placeholder="Repeat new password" />
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="exam-btn exam-btn-outline exam-btn-md" onClick={handleChangePassword}
                  disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}>
                  {pwSaving ? 'Changing…' : 'Change Password'}
                </button>
                {pwMsg && (
                  <span style={{ fontSize: 13, fontWeight: 500, color: (pwMsg.includes('match') || pwMsg.includes('8') || pwMsg.startsWith('Error')) ? 'var(--exam-error)' : 'var(--exam-success)' }}>
                    {pwMsg}
                  </span>
                )}
              </div>
            </div>
          </Section>

          {/* ── Accessibility ── */}
          <Section title="Accessibility (WCAG)" desc="Personalise the interface for comfort and accessibility. Changes apply instantly and are saved locally.">
            <Field label="Theme">
              <ChipSelector value={prefs.theme} onChange={(v) => updatePref('theme', v)}
                options={[
                  { value: 'light', label: 'Light', icon: '☀️' },
                  { value: 'dark', label: 'Dark', icon: '🌙' },
                  { value: 'high-contrast', label: 'High Contrast', icon: '⬛' },
                  { value: 'sepia', label: 'Soft Sepia', icon: '📜' },
                ]}
              />
            </Field>
            <Field label="Text Size">
              <ChipSelector value={prefs.fontSize} onChange={(v) => updatePref('fontSize', v)}
                options={[
                  { value: 'sm', label: 'Small' },
                  { value: 'md', label: 'Default' },
                  { value: 'lg', label: 'Large' },
                  { value: 'xl', label: 'X-Large' },
                ]}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', marginTop: 4 }}>
              <Toggle checked={prefs.reduceMotion} onChange={(v) => updatePref('reduceMotion', v)}
                label="Reduce motion and animations" />
              <Toggle checked={prefs.dyslexiaFont} onChange={(v) => updatePref('dyslexiaFont', v)}
                label="Dyslexia-friendly font" />
              <Toggle checked={prefs.focusIndicators} onChange={(v) => updatePref('focusIndicators', v)}
                label="Enhanced keyboard focus indicators" />
            </div>
          </Section>

          {/* ── Moodle Integration (read-only — managed in Tenant Admin) ── */}
          <Section title="Moodle Integration" desc="Moodle connection settings are managed centrally by your tenant administrator.">
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 18px', borderRadius: 10,
              background: moodleConfigured && moodleEnabled ? 'var(--exam-success-light, #f0fdf4)' : 'var(--exam-surface-alt)',
              border: '1px solid', borderColor: moodleConfigured && moodleEnabled ? 'var(--exam-success, #16a34a)' : 'var(--exam-border)',
              marginBottom: 16,
            }}>
              <span style={{ fontSize: 22 }}>{moodleConfigured && moodleEnabled ? '🟢' : '⚪'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--exam-text)', marginBottom: 2 }}>
                  {moodleConfigured && moodleEnabled ? 'Moodle integration is active' : 'Moodle integration is not configured'}
                </div>
                {moodleUrl && (
                  <div style={{ fontSize: 12, color: 'var(--exam-text-muted)' }}>{moodleUrl}</div>
                )}
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                background: moodleEnabled ? 'var(--exam-success, #16a34a)' : 'var(--exam-text-muted, #888)',
                color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {moodleEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--exam-text-muted)', lineHeight: 1.6 }}>
              To update the Moodle URL, token, or enable/disable the integration, go to{' '}
              <strong>Tenant Admin → Integrations</strong>. Changes there apply across all AcademicOS products.
            </div>
          </Section>

          {/* ── QBank Approval Policy ── */}
          <Section title="QBank Approval Policy" desc="Per-project approval is configured when submitting from the Questions panel.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { icon: '✓', title: 'Direct Approval', desc: 'Admin approves the QBank immediately without faculty review.' },
                { icon: '👥', title: 'Faculty Review', desc: 'QBank is sent to a faculty reviewer queue. Recommended for high-stakes exams.' },
              ].map((opt) => (
                <div key={opt.title} style={{ padding: '16px 18px', border: '1px solid var(--exam-border)', borderRadius: 10, background: 'var(--exam-surface-alt)' }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{opt.icon}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--exam-text)', marginBottom: 5 }}>{opt.title}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--exam-text-secondary)', lineHeight: 1.55 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
